import { useEffect, useRef, useState } from "react";

import { SearchBoxControl } from "@/Common/Components/Search/SearchBoxControl";
import { CommonHelper } from "@/Common/Helpers/CommonHelper";
import { HighlightHelper } from "@/Common/Helpers/HighlightHelper";
import { HighlightService } from "@/Common/Models/Services/HighlightService";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { SecondScreenNote } from "@/Patient/Components/SecondScreenNote";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";

import "./SecondScreenPage.scss";

const serachBoxControlRightInPX = 10;

function SecondScreenPage(): JSX.Element {
    function getEncounterList(): Encounter[] {
        return patientStore
            .PatientMedicalSummaryModels[patientStore.getCurrentPatientContactId()]
            ?.get("DisplayedEncounters")
            ?.toArray();
    }

    const secondScreenPageContainerClassName = "second-screen-page-container-to-highlight";
    const highlightService = useRef<HighlightService>(null);
    const secondScreenPageContainer = useRef<HTMLDivElement>(null);
    const topContainer: HTMLElement = document?.getElementsByTagName("html")[0];
    const patientStoreEventListenerId = useRef("");

    const [allElementsToHighlight, setAllElementsToHighlight] = useState<DOMRect[]>([]);
    const [noteModel, setNoteModel] = useState({
        patientId: patientStore.getCurrentPatientContactId(),
        patientBannerModel: patientStore.PatientBannerModels[patientStore.getCurrentPatientContactId()],
        notes: patientStore.PatientMedicalNotesCache[patientStore.getCurrentPatientContactId()],
        summary: patientStore.PatientMedicalSummaryModels[patientStore.getCurrentPatientContactId()],
        encounters: getEncounterList()
    });

    function updateModel(): void {
        if (noteModel.patientId !== patientStore.getCurrentPatientContactId() ||
            noteModel.notes !== patientStore.PatientMedicalNotesCache[patientStore.getCurrentPatientContactId()] ||
            noteModel.summary !== patientStore.PatientMedicalSummaryModels[patientStore.getCurrentPatientContactId()] ||
            noteModel.patientBannerModel !== patientStore.PatientBannerModels[patientStore.getCurrentPatientContactId()])
            setNoteModel({
                patientId: patientStore.getCurrentPatientContactId(),
                patientBannerModel: patientStore.PatientBannerModels[patientStore.getCurrentPatientContactId()],
                notes: patientStore.PatientMedicalNotesCache[patientStore.getCurrentPatientContactId()],
                summary: patientStore.PatientMedicalSummaryModels[patientStore.getCurrentPatientContactId()],
                encounters: getEncounterList()
            });
    }

    function traverseNodesToHighlight(node: ChildNode, isUserInfo: boolean, searchText: string, isOverflowing: boolean, width: number): DOMRect[] {
        const arr: DOMRect[] = [];

        if (node && !IsNullOrWhiteSpace(searchText)) {
            const nodeElement: Element = node as Element;
            const htmlElement: HTMLElement = node as HTMLElement;
            const htmlElementStyle = htmlElement ? getComputedStyle(htmlElement) : null;

            isUserInfo = isUserInfo || nodeElement?.classList?.contains("c2") || nodeElement?.classList?.contains("drag-container");
            isOverflowing = isOverflowing || htmlElementStyle?.overflow === "hidden";
            width = HighlightHelper.getWidth(htmlElement, isOverflowing, width);

            if (HighlightHelper.isBackgroundColorTransparent(htmlElementStyle?.backgroundColor)) {
                for (let i = 0; i < node?.childNodes?.length; i++) {
                    if (node.childNodes[i].nodeName === "#text") {
                        const textNode = node.childNodes[i] as Text;

                        if (isUserInfo && textNode?.length > 0) {
                            arr.push(...HighlightHelper.getTextRectsToHighlightWithChildNode(nodeElement, textNode, searchText, width));
                        }
                    }
                    else {
                        arr.push(...traverseNodesToHighlight(node.childNodes[i], isUserInfo, searchText, isOverflowing, width));
                    }
                }
            }
        }

        return arr;
    }

    function initializeHighlightService(): void {
        if (!highlightService.current && secondScreenPageContainer?.current) {
            highlightService.current = new HighlightService(
                secondScreenPageContainer.current as HTMLElement,
                topContainer as HTMLElement,
                secondScreenPageContainerClassName,
                noteModel.summary?.get("EncounterSearchTerm") ?? "",
                (searchText: string) => traverseNodesToHighlight(secondScreenPageContainer.current, false, searchText, false, HighlightHelper.invalidWidth)
            );

            highlightService?.current?.initialize();
        }
    }

    function executeHighlightService(searchText: string, isIndexReset: boolean): void {
        if (highlightService?.current) {
            highlightService.current?.execute(searchText, isIndexReset);
            setAllElementsToHighlight([...highlightService?.current?.allElementsToHighlight]);
        }
    }

    function terminateHighlightService(): void {
        if (highlightService?.current) {
            highlightService?.current?.terminate();
            highlightService.current = null;
        }
    }

    useEffect(
        () => {
            patientStore.Clear();
            patientStoreEventListenerId.current = patientStore.AddChangeListener(() => updateModel(), this, "second-patient-store");

            initializeHighlightService();
            executeHighlightService(noteModel.summary?.get("EncounterSearchTerm"), true);

            return () => {
                terminateHighlightService();
                patientStore.RemoveChangeListener(patientStoreEventListenerId.current);
            };
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(
        () => {
            if (highlightService?.current?.allElementsToHighlight && allElementsToHighlight && CommonHelper.isDOMRectArraysNotEqual(highlightService?.current?.allElementsToHighlight, allElementsToHighlight)) {
                executeHighlightService(noteModel?.summary?.get("EncounterSearchTerm"), false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [noteModel]
    );

    useEffect(
        () => {
            terminateHighlightService();
            initializeHighlightService();
            executeHighlightService(noteModel?.summary?.get("EncounterSearchTerm"), true);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [noteModel.patientId]
    );

    useEffect(
        () => {
            if (highlightService?.current && !IsNullOrUndefined(noteModel?.summary?.get("EncounterSearchTerm")) && highlightService?.current?.searchText !== noteModel.summary?.get("EncounterSearchTerm")) {
                executeHighlightService(noteModel.summary?.get("EncounterSearchTerm"), true);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [noteModel.summary]
    );

    return (
        <div
            ref={ref => secondScreenPageContainer.current = ref}
            className={"second-screen-page highlight-container " + secondScreenPageContainerClassName}
            id="secondScreenPage"
            data-cy="second-screen-page"
        >
            <>
                {!IsNullOrWhiteSpace(noteModel.summary?.get("EncounterSearchTerm") ?? "") && !isArrayNullOrUndefinedOrEmpty(allElementsToHighlight) && (
                    <SearchBoxControl
                        searchText={noteModel.summary?.get("EncounterSearchTerm") ?? ""}
                        container={topContainer}
                        elementsRects={allElementsToHighlight ?? []}
                        currentScrollTop={highlightService?.current?.currentScrollTop ?? 0}
                        rightInPX={serachBoxControlRightInPX}
                        encounterSearchTermChangedOperation={LocalPatientOperations.MedicalSummary.EncounterSearchTermChanged}
                        setHighlightServiceIndexCallbackFunc={(curIndex: number) => {
                            if (highlightService?.current) {
                                highlightService.current.index = curIndex;
                                highlightService?.current?.execute(null, false);
                            }
                        }}
                        scrollToRatio={2}
                        isFixed
                        isWindow
                    />
                )}
                <SecondScreenNote
                    patientId={noteModel.patientId}
                    patientBannerModel={noteModel.patientBannerModel}
                    noteModels={noteModel.notes}
                    summary={noteModel.summary}
                    encounters={noteModel.encounters}
                />
            </>
        </div>
    );
}

export { SecondScreenPage };
